import React, { useState } from 'react';
import styled from 'styled-components';
import SharedPageLayout from './SharedPageLayout';
import { theme } from '../theme';

const MarketplaceMainSection = styled.section`
  width: 100%;
  padding: 80px 5%;

  @media (max-width: 768px) {
    padding: 60px 4%;
  }

  @media (max-width: 480px) {
    padding: 40px 3%;
  }
`;

const MarketplaceMainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 60px;

  @media (max-width: 768px) {
    gap: 40px;
  }

  @media (max-width: 480px) {
    gap: 32px;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 32px;
  line-height: 40px;
  color: ${theme.colors.outchou_blue};
  text-align: center;
  margin: 0 0 20px 0;

  @media (max-width: 768px) {
    font-size: 28px;
    line-height: 36px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
    line-height: 32px;
  }
`;

const SectionDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: ${theme.colors.gray};
  text-align: center;
  margin: 0 0 40px 0;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 21px;
  }
`;

const FilterSection = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 40px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 16px;
  }
`;

const FilterButton = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  border: 2px solid ${props => props.active ? theme.colors.outchou_green : theme.colors.outchou_blue};
  border-radius: 25px;
  background: ${props => props.active ? theme.colors.outchou_green : 'transparent'};
  color: ${props => props.active ? theme.colors.white : theme.colors.outchou_blue};
  font-family: ${theme.fonts.body};
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.active ? theme.colors.outchou_green_hover : theme.colors.outchou_blue};
    color: ${theme.colors.white};
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 13px;
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  margin-top: 40px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 24px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ProductCard = styled.div`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 2px solid transparent;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
    border-color: ${theme.colors.outchou_green};
  }
`;

const ProductImage = styled.div<{ imageUrl: string }>`
  width: 100%;
  height: 200px;
  background-image: url(${props => props.imageUrl});
  background-size: cover;
  background-position: center;
  position: relative;
`;

const ProductBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: ${theme.colors.outchou_green};
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 12px;
`;

const ProductInfo = styled.div`
  padding: 24px;
`;

const ProductTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 18px;
  line-height: 26px;
  color: ${theme.colors.outchou_blue};
  margin: 0 0 8px 0;
`;

const ProductDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: ${theme.colors.gray};
  margin: 0 0 16px 0;
`;

const ProductPrice = styled.div`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 20px;
  color: ${theme.colors.outchou_green};
  margin-bottom: 16px;
`;

const AddToCartButton = styled.button`
  width: 100%;
  background: ${theme.colors.outchou_blue};
  border: none;
  border-radius: 8px;
  padding: 12px;
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 14px;
  color: white;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: ${theme.colors.outchou_blue_hover};
  }
`;

const ComingSoonBanner = styled.div`
  background: transparent;
  padding: 20px 40px;
  text-align: center;
  margin: 20px 0;

  @media (max-width: 768px) {
    padding: 15px 20px;
    margin: 15px 0;
  }
`;

const ComingSoonTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 28px;
  margin: 0;
  background: linear-gradient(90deg, ${theme.colors.outchou_blue} 0%, #3A4A9A 30%, #4A9A8A 70%, ${theme.colors.outchou_green} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: brightness(1.1);

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ComingSoonText = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 16px;
  margin: 0;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const ExternalShopsSection = styled.div`
  background: white;
  border-radius: 20px;
  padding: 60px 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);

  @media (max-width: 768px) {
    padding: 40px 24px;
  }

  @media (max-width: 480px) {
    padding: 32px 16px;
  }
`;

const ExternalShopsIntro = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const ExternalShopsTitle = styled.h2`
  font-family: ${theme.fonts.title};
  font-weight: 700;
  font-size: 28px;
  color: ${theme.colors.outchou_blue};
  margin: 0 0 24px 0;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ExternalShopsDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: ${theme.colors.gray};
  max-width: 800px;
  margin: 0 auto 16px auto;

  @media (max-width: 480px) {
    font-size: 14px;
    line-height: 22px;
  }
`;

const ExternalShopsDisclaimer = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: ${theme.colors.gray};
  max-width: 800px;
  margin: 0 auto;
  background: #f8f9fa;
  padding: 16px 20px;
  border-radius: 12px;
  border-left: 4px solid ${theme.colors.outchou_green};

  @media (max-width: 480px) {
    font-size: 13px;
    line-height: 20px;
  }
`;

const ShopCategory = styled.div`
  margin-bottom: 48px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ShopCategoryTitle = styled.h3`
  font-family: ${theme.fonts.title};
  font-weight: 600;
  font-size: 22px;
  color: ${theme.colors.outchou_blue};
  margin: 0 0 8px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid ${theme.colors.outchou_green};

  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const ShopCategoryDescription = styled.p`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: ${theme.colors.gray};
  margin: 0 0 24px 0;
  font-style: italic;
`;

const ShopList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ShopItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ShopName = styled.span`
  font-family: ${theme.fonts.body};
  font-weight: 600;
  font-size: 16px;
  color: ${theme.colors.outchou_blue};
`;

const ShopDescription = styled.span`
  font-family: ${theme.fonts.body};
  font-weight: 400;
  font-size: 14px;
  color: ${theme.colors.gray};
`;

const ShopLink = styled.a`
  font-family: ${theme.fonts.body};
  font-weight: 500;
  font-size: 14px;
  color: ${theme.colors.outchou_green};
  text-decoration: none;
  transition: color 0.2s ease;
  word-break: break-all;

  &:hover {
    color: ${theme.colors.outchou_green_hover};
    text-decoration: underline;
  }
`;

const MarketplacePage: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const categories = [
    { id: 'all', label: 'Tous' },
    { id: 'pottery', label: 'Céramique' },
    { id: 'textiles', label: 'Textiles' },
    { id: 'jewelry', label: 'Bijoux' },
    { id: 'woodwork', label: 'Bois' },
    { id: 'metalwork', label: 'Métal' }
  ];

  const products = [
    {
      id: 1,
      title: "Pot en céramique traditionnel",
      description: "Pot en céramique fabriqué selon les techniques ancestrales de Kabylie",
      price: "2,500 DA",
      image: "/shared_assets/images/ceramique.jpeg",
      category: "pottery",
      badge: "Artisanal"
    },
    {
      id: 2,
      title: "Tapis berbère",
      description: "Tapis tissé à la main avec des motifs traditionnels berbères",
      price: "15,000 DA",
      image: "/shared_assets/images/tradition.jpeg",
      category: "textiles",
      badge: "Authentique"
    },
    {
      id: 3,
      title: "Bijoux en argent",
      description: "Collier et boucles d'oreilles en argent massif, style traditionnel",
      price: "8,500 DA",
      image: "/shared_assets/images/fibule.jpeg",
      category: "jewelry",
      badge: "Unique"
    },
    {
      id: 4,
      title: "Meuble en bois sculpté",
      description: "Coffre traditionnel en bois de cèdre sculpté à la main",
      price: "25,000 DA",
      image: "/shared_assets/images/tradition.jpeg",
      category: "woodwork",
      badge: "Exclusif"
    }
  ];

  const filteredProducts = activeFilter === 'all' 
    ? products 
    : products.filter(product => product.category === activeFilter);

  return (
    <SharedPageLayout 
      heroType="search"
      backgroundImage="/shared_assets/images/navigation/marketplace.jpeg"
      heroTitle="Le Hanout"
      heroSubtitle="En attendant l’ouverture de notre boutique en ligne, on commence déjà par ce qu’on sait faire de mieux : partager."
    >
      <MarketplaceMainSection>
        <MarketplaceMainContent>
          <ExternalShopsSection>
            <ExternalShopsIntro>
              <ExternalShopsTitle>En attendant l'ouverture du Hanout…</ExternalShopsTitle>
              <ExternalShopsDescription>
                Voici une sélection de boutiques externes, indépendantes de Outchou, que nous aimons particulièrement.
              </ExternalShopsDescription>
              <ExternalShopsDisclaimer>
                👉 Outchou ne gère pas ces boutiques ni les commandes : nous te partageons simplement des adresses inspirantes à découvrir.
              </ExternalShopsDisclaimer>
            </ExternalShopsIntro>

            <ShopCategory>
              <ShopCategoryTitle>1. Mode moderne & streetwear d'inspiration algérienne</ShopCategoryTitle>
              <ShopCategoryDescription>Vêtements du quotidien, t-shirts, pièces streetwear et looks modernes avec une touche dz.</ShopCategoryDescription>
              <ShopList>
                <ShopItem>
                  <ShopName>Bzima</ShopName>
                  <ShopDescription>Mode féminine moderne inspirée de l'Algérie.</ShopDescription>
                  <ShopLink href="https://bzima.com" target="_blank" rel="noopener noreferrer">https://bzima.com</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Dar Deeja</ShopName>
                  <ShopDescription>Pièces contemporaines avec détails et motifs algériens.</ShopDescription>
                  <ShopLink href="https://dardeeja.com" target="_blank" rel="noopener noreferrer">https://dardeeja.com</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Intique</ShopName>
                  <ShopDescription>Marque basée au Royaume-Uni, entre diaspora, héritage et modernité.</ShopDescription>
                  <ShopLink href="https://intique.co.uk" target="_blank" rel="noopener noreferrer">https://intique.co.uk</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Fennec Road Trip</ShopName>
                  <ShopDescription>Streetwear et visuels autour des symboles et paysages algériens.</ShopDescription>
                  <ShopLink href="https://fennecroadtrip.shop" target="_blank" rel="noopener noreferrer">https://fennecroadtrip.shop</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Algerian Touch</ShopName>
                  <ShopDescription>T-shirts, sweats & accessoires aux couleurs de l'Algérie.</ShopDescription>
                  <ShopLink href="https://algeriantouch.fr" target="_blank" rel="noopener noreferrer">https://algeriantouch.fr</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>3ami Shop</ShopName>
                  <ShopDescription>Univers foot & culture populaire algérienne, humour et second degré.</ShopDescription>
                  <ShopLink href="https://3ami.shop" target="_blank" rel="noopener noreferrer">https://3ami.shop</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Fennecs Stars</ShopName>
                  <ShopDescription>Textiles et accessoires pour supporters & amoureux de l'équipe nationale.</ShopDescription>
                  <ShopLink href="https://fennecsstars.fr" target="_blank" rel="noopener noreferrer">https://fennecsstars.fr</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>La Casbah Paris</ShopName>
                  <ShopDescription>Marque parisienne qui revisite la culture algérienne en mode & lifestyle.</ShopDescription>
                  <ShopLink href="https://www.lacasbahparis.fr" target="_blank" rel="noopener noreferrer">https://www.lacasbahparis.fr</ShopLink>
                </ShopItem>
              </ShopList>
            </ShopCategory>

            <ShopCategory>
              <ShopCategoryTitle>2. Artisanat & déco : maison, objets, savoir-faire</ShopCategoryTitle>
              <ShopCategoryDescription>Objets pour la maison, pièces en cuivre, tissages, déco et artisanat revisité.</ShopCategoryDescription>
              <ShopList>
                <ShopItem>
                  <ShopName>L'Art du Cuivre</ShopName>
                  <ShopDescription>Atelier autour du travail du cuivre, héritage artisanal algérien.</ShopDescription>
                  <ShopLink href="https://www.instagram.com/artducuivre/" target="_blank" rel="noopener noreferrer">https://www.instagram.com/artducuivre/</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Les Merveilles d'Algérie</ShopName>
                  <ShopDescription>Sélection d'artisanat et objets déco inspirés des régions d'Algérie.</ShopDescription>
                  <ShopLink href="https://www.les-merveilles-dalgerie.fr" target="_blank" rel="noopener noreferrer">https://www.les-merveilles-dalgerie.fr</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>L'Artisan Algérien</ShopName>
                  <ShopDescription>Plateforme dédiée à l'artisanat algérien (objets, déco, idées cadeaux).</ShopDescription>
                  <ShopLink href="https://lartisan-algerien.com" target="_blank" rel="noopener noreferrer">https://lartisan-algerien.com</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Paribanou</ShopName>
                  <ShopDescription>Univers oriental & maghrébin, avec une place importante pour l'Algérie.</ShopDescription>
                  <ShopLink href="https://www.paribanou.com" target="_blank" rel="noopener noreferrer">https://www.paribanou.com</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Artisan DZ</ShopName>
                  <ShopDescription>Boutique en ligne d'artisanat moderne (déco, cadeaux, objets du quotidien).</ShopDescription>
                  <ShopLink href="https://artisan-dz.flexdz.store/" target="_blank" rel="noopener noreferrer">https://artisan-dz.flexdz.store/</ShopLink>
                </ShopItem>
              </ShopList>
            </ShopCategory>

            <ShopCategory>
              <ShopCategoryTitle>3. Bijoux & accessoires revisités</ShopCategoryTitle>
              <ShopCategoryDescription>Bijoux amazigh/algériens modernisés, entre tradition et design contemporain.</ShopCategoryDescription>
              <ShopList>
                <ShopItem>
                  <ShopName>Glamazigh</ShopName>
                  <ShopDescription>Bijoux inspirés de l'héritage amazigh, revisités pour le quotidien.</ShopDescription>
                  <ShopLink href="https://glamazigh.com" target="_blank" rel="noopener noreferrer">https://glamazigh.com</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Azrar</ShopName>
                  <ShopDescription>Bijoux et accessoires mêlant symboles berbères et design moderne.</ShopDescription>
                  <ShopLink href="https://azrar.fr" target="_blank" rel="noopener noreferrer">https://azrar.fr</ShopLink>
                </ShopItem>
              </ShopList>
            </ShopCategory>

            <ShopCategory>
              <ShopCategoryTitle>4. Tenues traditionnelles & habillés pour hommes</ShopCategoryTitle>
              <ShopCategoryDescription>Qamis, gandouras, costumes d'inspiration traditionnelle pour les grandes occasions.</ShopCategoryDescription>
              <ShopList>
                <ShopItem>
                  <ShopName>Sofanna</ShopName>
                  <ShopDescription>Tenues élégantes pour hommes, entre classique et contemporain.</ShopDescription>
                  <ShopLink href="https://sofanna.com" target="_blank" rel="noopener noreferrer">https://sofanna.com</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Ziri Couture</ShopName>
                  <ShopDescription>Vêtements masculins inspirés des coupes et broderies algériennes.</ShopDescription>
                  <ShopLink href="https://ziricouture.com" target="_blank" rel="noopener noreferrer">https://ziricouture.com</ShopLink>
                </ShopItem>
              </ShopList>
            </ShopCategory>

            

            <ShopCategory>
              <ShopCategoryTitle>5. Jeux & culture ludique</ShopCategoryTitle>
              <ShopCategoryDescription>Jeux de société, cartes, supports ludiques autour de la culture dz & maghrébine.</ShopCategoryDescription>
              <ShopList>
                <ShopItem>
                  <ShopName>El Ghechache</ShopName>
                  <ShopDescription>Jeux de carte "qui suis-je ?" sous forme de quiz version algérienne.</ShopDescription>
                  <ShopLink href="https://www.instagram.com/el_ghechache" target="_blank" rel="noopener noreferrer">https://www.instagram.com/el_ghechache</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>NIF213</ShopName>
                  <ShopDescription>Jeux de cartes sous forme de quiz autour de l'identité et de la culture algérienne.</ShopDescription>
                  <ShopLink href="https://www.nif213.com" target="_blank" rel="noopener noreferrer">https://www.nif213.com</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Fendada</ShopName>
                  <ShopDescription>Jeux de cartes (dominos et 54 cartes) illustrées par des tenues algériennes.</ShopDescription>
                  <ShopLink href="https://fendada.art/" target="_blank" rel="noopener noreferrer">https://fendada.art/</ShopLink>
                </ShopItem>
              </ShopList>
            </ShopCategory>

            <ShopCategory>
              <ShopCategoryTitle>6. Plateformes maghrébines avec une section Algérie</ShopCategoryTitle>
              <ShopCategoryDescription>Pas uniquement algériennes, mais avec une belle place pour le Maghreb et l'Algérie.</ShopCategoryDescription>
              <ShopList>
                <ShopItem>
                  <ShopName>Maghreb Souk</ShopName>
                  <ShopDescription>Marketplace dédiée au Maghreb, avec une section Algérie.</ShopDescription>
                  <ShopLink href="https://maghrebsouk.com/collections/algerie" target="_blank" rel="noopener noreferrer">https://maghrebsouk.com/collections/algerie</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Les Possibles</ShopName>
                  <ShopDescription>Sélection de marques issues des diasporas et du Sud global.</ShopDescription>
                  <ShopLink href="https://lespossibles.shop" target="_blank" rel="noopener noreferrer">https://lespossibles.shop</ShopLink>
                </ShopItem>
                <ShopItem>
                  <ShopName>Gosto Wear</ShopName>
                  <ShopDescription>Textile & streetwear autour des cultures maghrébines.</ShopDescription>
                  <ShopLink href="https://gosto-wear.myspreadshop.fr" target="_blank" rel="noopener noreferrer">https://gosto-wear.myspreadshop.fr</ShopLink>
                </ShopItem>
              </ShopList>
            </ShopCategory>
          </ExternalShopsSection>
        </MarketplaceMainContent>
      </MarketplaceMainSection>
    </SharedPageLayout>
  );
};

export default MarketplacePage;
